/* =========================================================================
   dko-autofit.js  ·  Centrado real del producto dentro de la tarjeta
   -------------------------------------------------------------------------
   EL PROBLEMA
   Las fotos del catalogo son cuadradas pero el producto no esta centrado en
   ellas. CSS centra el archivo, no puede ver el producto.

   LO QUE HACE
   Para cada foto de tarjeta visible: la relee en un canvas chico, encuentra
   la caja del producto (todo lo no blanco/transparente) y escribe en el
   <img> un translate + scale que centra esa caja en la ventana visible de
   la tarjeta con margen parejo. dko-theme.css aplica las variables.

   ---------------------------------------------------------------------------
   AJUSTE DKo 30/09/2026 · fotos en blanco en hover y a veces la primera
   ---------------------------------------------------------------------------
   SINTOMA: al pasar el mouse por una tarjeta la segunda foto quedaba en
   blanco; a veces tambien la primera, sin hover. Pasaba a unas personas y a
   otras no. Se verifico en consola que las fotos ESTABAN cargadas
   (complete=true, naturalWidth>0, scrollLeft=0). O sea, no era red ni lazy.

   CAUSA REAL: este script calculaba la traslacion midiendo la posicion EN
   PANTALLA del <img> contra la ventana visible (.card-gallery). Dentro del
   carrusel interno de la tarjeta, la posicion en pantalla de una foto depende
   de que diapositiva este desplazada a la vista en ese instante. Si se media
   la foto 2 mientras la visible era la 1, la foto 2 estaba una tarjeta a la
   derecha y el script la "corregia" con tx ~ -257px. Al llegar por hover ya
   venia trasladada fuera del recorte: blanco con la foto cargada.

   Dos momentos producian la medicion equivocada:
     1. El analisis del canvas es asincrono. Hover -> entra la foto 2 -> se
        procesa -> el mouse ya salio cuando termina -> se mide desplazada.
        Quedaba marcada como hecha y nunca se recalculaba.
     2. El ResizeObserver guardaba UNA sola imagen por tarjeta (la ultima
        pisaba a las anteriores) y en cualquier reflow la re-aplicaba con el
        scroller en donde estuviera. Asi se rompia tambien la primera foto.

   Por eso era irreproducible: dependia de donde estaba el mouse cuando
   termino un canvas y de cuantos reflows tuvo cada pantalla.

   ARREGLO:
     - La referencia HORIZONTAL es la propia diapositiva del <img>
       (slideshow-slide), que mide lo mismo que la ventana y viaja con la
       foto, asi que el resultado no depende del scroll del carrusel.
     - La referencia VERTICAL sigue siendo la ventana visible (.card-gallery),
       que es la que recorta en los carruseles de la home (nota 14/09) y no
       se mueve con el scroll horizontal.
     - El ResizeObserver observa cada <img>, no la tarjeta. Tambien sirve
       para recalcular cuando una diapositiva pasa de content-visibility
       hidden a visible (su caja pasa de 0 a real).
     - Si al medir la caja es 0 (diapositiva aun sin layout) no se marca como
       hecha: se reintenta cuando el ResizeObserver le de tamano.

   LIMITES: fotos sobre fondo de color no se tocan; tope de ampliacion 1.5x;
   es correccion visual, el arreglo definitivo es recortar en origen.
   AUTOR: implementacion propia.
   ========================================================================= */

(function () {
  'use strict';

  var CFG = {
    margin: 0.06,
    maxScale: 1.5,
    minGain: 0.03,
    sample: 160,
    white: 238,
    alpha: 24,
    selector: '#MainContent .card-gallery .product-media > img',
    clipSelector: '.card-gallery',
    slideSelector: 'slideshow-slide'
  };

  if (!('IntersectionObserver' in window)) return;

  var cache = {};
  var pending = {};

  function analyze(src, cb) {
    if (cache[src] !== undefined) return cb(cache[src]);
    if (pending[src]) { pending[src].push(cb); return; }
    pending[src] = [cb];
    var done = function (bb) {
      cache[src] = bb;
      var list = pending[src]; delete pending[src];
      list.forEach(function (f) { f(bb); });
    };
    var im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = function () {
      try {
        var w = im.naturalWidth, h = im.naturalHeight;
        if (!w || !h) return done(false);
        var sc = Math.min(1, CFG.sample / Math.max(w, h));
        var cw = Math.max(1, Math.round(w * sc)), ch = Math.max(1, Math.round(h * sc));
        var c = document.createElement('canvas');
        c.width = cw; c.height = ch;
        var ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(im, 0, 0, cw, ch);
        var d = ctx.getImageData(0, 0, cw, ch).data;
        var x0 = cw, y0 = ch, x1 = -1, y1 = -1;
        for (var y = 0; y < ch; y++) {
          for (var x = 0; x < cw; x++) {
            var i = (y * cw + x) * 4;
            if (d[i + 3] < CFG.alpha) continue;
            if (d[i] >= CFG.white && d[i + 1] >= CFG.white && d[i + 2] >= CFG.white) continue;
            if (x < x0) x0 = x; if (x > x1) x1 = x;
            if (y < y0) y0 = y; if (y > y1) y1 = y;
          }
        }
        if (x1 < 0) return done(false);
        done({ x0: x0 / cw, y0: y0 / ch, x1: (x1 + 1) / cw, y1: (y1 + 1) / ch });
      } catch (e) { done(false); }
    };
    im.onerror = function () { done(false); };
    im.src = src;
  }

  function neutralize(img) {
    img.style.setProperty('--dko-fit', 'translate(0px, 0px) scale(1)');
    img.style.setProperty('--dko-fit-origin', '50% 50%');
  }

  /* Devuelve true si pudo medir y aplicar; false si la caja aun no existe. */
  function apply(img, bb) {
    var clip = img.closest(CFG.clipSelector) || img.closest('.product-media-container') || img.parentElement;
    /* 30/09: la referencia horizontal es la diapositiva, no la ventana. */
    var slide = img.closest(CFG.slideSelector) || clip;
    var nw = img.naturalWidth, nh = img.naturalHeight;
    if (!nw || !nh) return false;

    /* 1. Medir con el ajuste en identidad y sin animar */
    var prevT = img.style.transition;
    img.style.transition = 'none';
    neutralize(img);
    void img.offsetWidth;

    var ir = img.getBoundingClientRect();
    var cr = clip.getBoundingClientRect();
    var sr = slide.getBoundingClientRect();
    if (!ir.width || !ir.height || !cr.width || !cr.height || !sr.width) {
      img.style.transition = prevT;
      return false;
    }

    /* 2. Donde queda dibujada la foto dentro del <img> (object-fit: contain) */
    var r = Math.min(ir.width / nw, ir.height / nh);
    var rw = nw * r, rh = nh * r;
    var ox = (ir.width - rw) / 2, oy = (ir.height - rh) / 2;

    /* 3. Producto: centro (en px del <img>) y tamano dibujado */
    var cx = ox + (bb.x0 + bb.x1) / 2 * rw;
    var cy = oy + (bb.y0 + bb.y1) / 2 * rh;
    var pw = (bb.x1 - bb.x0) * rw;
    var ph = (bb.y1 - bb.y0) * rh;

    /* 4. Escala: que quepa con margen en la VENTANA VISIBLE, por ambos ejes */
    var kx = (cr.width  * (1 - 2 * CFG.margin)) / pw;
    var ky = (cr.height * (1 - 2 * CFG.margin)) / ph;
    var k = Math.min(kx, ky, CFG.maxScale);
    if (k < 1) k = 1;

    /* 5. Traslacion: centro del producto al centro de la ventana.
          Horizontal contra la DIAPOSITIVA (viaja con la foto, no depende del
          scroll del carrusel). Vertical contra la VENTANA (la que recorta). */
    var productX = ir.left + cx, productY = ir.top + cy;
    var targetX = sr.left + sr.width / 2, targetY = cr.top + cr.height / 2;
    var tx = targetX - productX;
    var ty = targetY - productY;

    if (Math.abs(tx) < cr.width * CFG.minGain &&
        Math.abs(ty) < cr.height * CFG.minGain &&
        k < 1 + CFG.minGain) {
      img.style.removeProperty('--dko-fit');
      img.style.removeProperty('--dko-fit-origin');
      delete img.dataset.dkoFit;
      void img.offsetWidth;
      img.style.transition = prevT;
      return true;
    }

    img.style.setProperty('--dko-fit-origin', cx.toFixed(1) + 'px ' + cy.toFixed(1) + 'px');
    img.style.setProperty('--dko-fit', 'translate(' + tx.toFixed(1) + 'px, ' + ty.toFixed(1) + 'px) scale(' + k.toFixed(3) + ')');
    img.dataset.dkoFit = '1';
    void img.offsetWidth;
    img.style.transition = prevT;
    return true;
  }

  /* 30/09: se observa cada <img>. Antes se observaba la tarjeta y guardaba
     una sola imagen por tarjeta, pisando a las demas. */
  var ro = ('ResizeObserver' in window) ? new ResizeObserver(function (entries) {
    entries.forEach(function (e) {
      var img = e.target;
      if (img._dkoBB) apply(img, img._dkoBB);
    });
  }) : null;

  function process(img) {
    if (img.dataset.dkoFitDone === '1') return;
    if (img.closest('[data-dko-skip]')) return;
    var run = function () {
      var src = img.currentSrc || img.src;
      if (!src) return;
      img.dataset.dkoFitDone = '1';
      analyze(src, function (bb) {
        if (!bb) return;
        img._dkoBB = bb;
        apply(img, bb);
        if (ro) ro.observe(img);
      });
    };
    if (img.complete && img.naturalWidth > 0) run();
    else img.addEventListener('load', run, { once: true });
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      process(e.target);
    });
  }, { rootMargin: '200px 0px' });

  function scan(root) {
    var imgs = (root || document).querySelectorAll(CFG.selector);
    Array.prototype.forEach.call(imgs, function (img) {
      if (img.dataset.dkoFitSeen === '1') return;
      img.dataset.dkoFitSeen = '1';
      io.observe(img);
    });
  }

  if (!ro) {
    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () {
        Array.prototype.forEach.call(document.querySelectorAll(CFG.selector + '[data-dko-fit-done="1"]'), function (img) {
          if (img._dkoBB) apply(img, img._dkoBB);
        });
      }, 150);
    }, { passive: true });
  }

  function init() { scan(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  var mo = new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) if (muts[i].addedNodes.length) { scan(); return; }
  });
  var main = document.getElementById('MainContent');
  if (main) mo.observe(main, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();